import React, { useMemo } from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import filter from 'lodash/filter';
import {
  AppleButton as Auth0AppleButton,
  GoogleButton as Auth0GoogleButton,
} from '@nerdwallet/nw-auth-buttons';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import LinkGenerator from '@nerdwallet/nw-auth-links';
import { Box, Link, Text, Button } from '@nerdwallet/currency';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { FinishType } from '@nerdwallet/react-apply-now';
import appConfig from '@nerdwallet/app-config';
import analytics from '@nerdwallet/analytics';
import {
  baseSegmentAnalytics,
  baseSegmentAnalyticsWithCallingAttrs,
} from '../../lib/utils';

import styles from './auth0-buttons.module.less';
import {
  segmentImpressionId,
  trackSegmentTrackEvent,
} from '../../lib/segment-analytics';

const isTestEnv = process?.env?.NODE_ENV === 'test';

// Usage:
// The only required component is "Auth0Buttons.Container", then add each
// desired authentication method as shown below.

// <Auth0Buttons.Container>
//   <Auth0Buttons.AppleButton />
//   <Auth0Buttons.GoogleButton />
//   <Auth0Buttons.EmailLink />
//   <Auth0Buttons.LegalLinks />
//   <Auth0Buttons.GenericButton redirectTo={redirectTo} />
// </Auth0Buttons.Container>

// getAuth0Params to be used in linkGenerator.register method
const getAuthParams = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = {
    ...segmentTrackingAttributes,
    redirectTo: redirectTo || `${process.env.SITE_BASE_URL}/home/dashboard`,
  };
  return authParams;
};

const combinedSegmentAnalytics = {
  ...baseSegmentAnalytics,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_name: appConfig?._meta?.deployable,
  // eslint-disable-next-line no-underscore-dangle
  calling_deployable_version: appConfig?._meta?.version,
};

// Contains the main auth0 logic and shares relevant data with each type of
// Auth0 sign in method
const Container = ({
  loginTrkLocation,
  loginTrkSubLocation,
  loginTrkFormat,
  children,
  flatOutline,
  displayBlock,
  location,
}) => {
  const pageConfig = filter(
    flatOutline,
    (component) => component.type === 'Page'
  )[0];
  const contentId = Number(pageConfig?.contentId);
  const { pageName, pageViewId } = useAnalyticsPageView();
  const pathLocation = loginTrkLocation || location?.pathname;
  const segmentTrackingAttributes = useMemo(
    () => ({
      ...baseSegmentAnalyticsWithCallingAttrs,
      driverPageName: pageName,
      driverLocation: pathLocation,
      driverPageViewId: pageViewId,
      driverSubLocation: loginTrkSubLocation,
      driverContentId: String(contentId),
      driverFlowName: 'basic',
      driverContentSource: 'wp',
      driverElement: 'link',
    }),
    [pageName, pathLocation, pageViewId, loginTrkSubLocation, contentId]
  );

  const handleClick = () => {
    trackSegmentTrackEvent('Element Interaction', {
      entity_name: 'sign_in_button',
      location: pathLocation,
      sub_location: loginTrkSubLocation,
      cta_type: 'sign_in_button',
      cta_format: loginTrkFormat,
      ...combinedSegmentAnalytics,
    });

    if (!isTestEnv) {
      analytics?.track('element_interacted', {
        action: 'select',
        entity_name: 'sign_in_button',
        impression_id: segmentImpressionId,
        element_name: 'sign_in_button',
        interaction_type: 'press',
        cta_type: 'offer_link',
        finish_type: FinishType.EXTERNAL_APPLICATION,
      });
    }
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        segmentTrackingAttributes,
      });
    }
    return child;
  });

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-testid="auth0-container"
      className={styles.container}
      style={{ display: displayBlock ? 'block' : 'flex' }}
      onClick={handleClick}
    >
      {childrenWithProps}
    </div>
  );
};

const ConnectedContainer = compose(
  withRouter,
  connect(
    createStructuredSelector({
      flatOutline: ({ builder }) => builder?.flatOutline,
    })
  )
)(Container);

// Renders AppleButton authentication method.
const AppleButton = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = getAuthParams({ segmentTrackingAttributes, redirectTo });
  return <Auth0AppleButton buttonWidth={400} {...authParams} />;
};

// Renders GoogleButton authentication method.
const GoogleButton = ({ segmentTrackingAttributes, redirectTo }) => {
  const authParams = getAuthParams({ segmentTrackingAttributes, redirectTo });
  return <Auth0GoogleButton {...authParams} />;
};

// Renders Email link authentication method.
const EmailLink = ({ segmentTrackingAttributes, redirectTo }) => {
  const handleEmailSignIn = (e) => {
    e.preventDefault();
    const updatedSegmentTrackingAttributes = {
      ...segmentTrackingAttributes,
      driverElement: 'button',
    };
    const authParams = getAuthParams({
      segmentTrackingAttributes: updatedSegmentTrackingAttributes,
      redirectTo,
    });
    const linkGenerator = new LinkGenerator();
    const registrationLink = linkGenerator.register(authParams);
    window.location.href = registrationLink;
  };

  return (
    <Text size="small">
      <span>or continue with </span>
      <Link aria-label="email" onClick={handleEmailSignIn}>
        email
      </Link>
    </Text>
  );
};

// Renders Generic button authentication method
const GenericButton = ({
  segmentTrackingAttributes,
  redirectTo,
  customCtaText,
  buttonStyle,
  displayBlock,
  className,
  configParams,
}) => {
  const updatedSegmentTrackingAttributes = {
    ...segmentTrackingAttributes,
    driverElement: 'button',
  };

  const handleEmailSignIn = (e) => {
    e.preventDefault();

    const authParams = getAuthParams({
      segmentTrackingAttributes: updatedSegmentTrackingAttributes,
      redirectTo,
    });
    const linkGenerator = new LinkGenerator(configParams);
    const registrationLink = linkGenerator.register(authParams);
    window.location.href = registrationLink;
  };

  return (
    <Button
      onClick={handleEmailSignIn}
      {...buttonStyle}
      block={displayBlock}
      className={className}
    >
      {customCtaText || 'SIGN IN OR CREATE ACCOUNT'}
    </Button>
  );
};

// Renders Legal Links
const LegalLinks = () => {
  return (
    <Box display="flex" className={styles.legalLinks}>
      <Text size="small">
        By signing up, I agree to NerdWallet&apos;s <br />
        <Link href="https://nerdwallet.com/p/terms-of-use">
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link href="https://nerdwallet.com/p/privacy-policy">
          Privacy Policy
        </Link>
      </Text>
    </Box>
  );
};

Container.propTypes = {
  flatOutline: P.object,
  loginTrkLocation: P.string,
  loginTrkSubLocation: P.string,
  loginTrkFormat: P.string,
  displayBlock: P.bool,
  children: P.node,
  location: P.object,
};

AppleButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

GoogleButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

EmailLink.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
};

GenericButton.propTypes = {
  segmentTrackingAttributes: P.object,
  redirectTo: P.string,
  customCtaText: P.string,
  buttonStyle: P.object,
  displayBlock: P.bool,
  className: P.string,
  configParams: P.shape({
    loginHeading: P.string,
    registrationHeading: P.string,
  }),
};

GenericButton.defaultProps = {
  buttonStyle: {
    primary: true,
  },
  displayBlock: false,
  configParams: {
    loginHeading: '',
    registrationHeading: '',
  },
};

Container.defaultProps = {
  redirectTo: '',
  loginTrkLocation: '',
  loginTrkFormat: '',
  children: null,
  displayBlock: false,
};

export const Auth0Buttons = {
  Container: ConnectedContainer,
  AppleButton,
  GoogleButton,
  EmailLink,
  GenericButton,
  LegalLinks,
};
